import dayjs, { Dayjs } from "dayjs";
import cloneDeep from "lodash.clonedeep";
import { FC, useEffect, useState } from "react";

import { Box, Checkbox, Chip, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, OutlinedInput, Select, Stack, TextField, Tooltip, Typography } from "@mui/material";

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { BoothClientModel } from "../../../models/ReportPageModels";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { TrackingOptionModel } from "../../Admin/pages/Xero/Tracking/Tracking";
import { BillingDbProductModel, BillingTaxModel, CommissionModel, DisplayFeedModel, ProductModel } from "../BillingSettings/BillingSettings";

interface BillingProductProps {
    allProducts: BillingDbProductModel[];
    productNames: string[];

    clientCurrency: string;
    allTaxes: BillingTaxModel[];
    allBoothIds: BoothClientModel[];
    allCommission: CommissionModel[];
    allPortalFeeds: DisplayFeedModel[];
    allXeroTracking: TrackingOptionModel[];

    product: ProductModel;

    removeProduct: (param: ProductModel) => void;
    duplicateProduct: (param: ProductModel) => void;
    moveUpProduct: (param: ProductModel) => void;
    moveDownProduct: (param: ProductModel) => void;
}

const BillingProduct: FC<BillingProductProps> = ({
    allProducts, productNames,

    clientCurrency, allTaxes, allBoothIds, allCommission, allPortalFeeds, allXeroTracking,
    product,

    removeProduct, duplicateProduct, moveUpProduct, moveDownProduct }) => {

    const [productCode, setProductCode] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [quantity, setQuantity] = useState<number>(0);
    const [price, setPrice] = useState<number>(0);
    const [tax, setTax] = useState<number>(1);
    const [boothIdFilter, setBoothIdFilter] = useState<string[]>([]);
    const [allowFree, setAllowFree] = useState<number>(0);
    const [commission, setCommission] = useState<number[]>([]);
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [portalFeed, setPortalFeed] = useState<number[]>([]);
    const [xeroTracking, setXeroTracking] = useState<string>("noselect");
    const [hide, setHide] = useState<boolean>(false);

    const [quantityVisibility, setQuantityVisibility] = useState<boolean>(true);
    const [priceVisibility, setPriceVisibility] = useState<boolean>(true);
    const [taxVisibility, setTaxVisibility] = useState<boolean>(true);
    const [boothIdFilterVisibility, setBoothIdFilterVisibility] = useState<boolean>(true);
    const [allowFreeVisibility, setAllowFreeVisibility] = useState<boolean>(false);
    const [allowCommissionVisibility, setAllowCommissionVisibility] = useState<boolean>(false);
    const [showStartDate, setShowStartDate] = useState<boolean>(false);
    const [allowHideVisibility, setAllowHideVisibility] = useState<boolean>(false);

    const [isPriceError, setIsPriceError] = useState<boolean>(false)
    const [trackingNotSet, setTrackingNotSet] = useState<boolean>(false);

    useEffect(() => {
        setProductCode(product.productCode);
        setDescription(product.description);
        setQuantity(product.quantity);
        setPrice(product.price);
        setTax(product.tax);
        setBoothIdFilter(product.boothIdFilter);
        setAllowFree(product.allowFree);
        setCommission(product.commission);
        setPortalFeed(product.portalFeed);
        setHide(product.hide);

        if (product.startDate !== null) {
            setStartDate(dayjs(product.startDate));
        }

        if (product.xeroTracking === null) {
            setXeroTracking("noselect");
            setTrackingNotSet(true);
        }
        else if (typeof product.xeroTracking === "string") {
            setXeroTracking("noselect");
            setTrackingNotSet(true);
        }
        else {
            setXeroTracking(JSON.stringify(product.xeroTracking));
            setTrackingNotSet(false);
        }

        CheckProduct();
    }, [product])

    function CheckProduct() {
        if (product.productCode === "")
            return;

        var dbProductSettings = {} as BillingDbProductModel;
        for (var i = 0; i < allProducts.length; i++) {
            if (allProducts[i].productCode === product.productCode) {
                dbProductSettings = cloneDeep(allProducts[i]);
                break;
            }
        }

        // check what fields should be available
        CheckSettings(dbProductSettings);
        // set default values for new created product
        SetValuesForNewProduct(dbProductSettings);
    }

    function CheckSettings(productSettings: BillingDbProductModel) {
        if (productSettings.billingPer === "/month") {
            setQuantityVisibility(true);
            setShowStartDate(true);
        }
        else {
            setQuantityVisibility(false);
        }

        if (productSettings.unitPrice === -1)
            setPriceVisibility(false);
        else
            setPriceVisibility(true);

        if (productSettings.taxRate === 1)
            setTaxVisibility(true)
        else
            setTaxVisibility(false)

        if (productSettings.boothIdFilter === 1)
            setBoothIdFilterVisibility(true)
        else
            setBoothIdFilterVisibility(false)

        if (productSettings.allowFree === 1)
            setAllowFreeVisibility(true);
        else
            setAllowFreeVisibility(false);

        if (productSettings.allowCommission === 1)
            setAllowCommissionVisibility(true);
        else
            setAllowCommissionVisibility(false);

        if (productSettings.allowHide === 1)
            setAllowHideVisibility(true);
        else
            setAllowHideVisibility(false);
    }

    function SetValuesForNewProduct(productSettings: BillingDbProductModel) {
        if (product.description === "") {
            product.description = productSettings.description
            setDescription(productSettings.description)
        }

        if (product.price === -1) {
            product.price = productSettings.unitPrice;
            setPrice(productSettings.unitPrice);
        }
    }

    function SetProductCode(event: any) {
        var value = event.target.value;
        product.productCode = value
        setProductCode(value);

        CheckProduct();
    }

    function SetProductDescription(event: any) {
        var value = event.target.value;
        product.description = value
        setDescription(value);
    }

    function SetProductQuantity(event: any) {
        var value = Number(event.target.value);
        product.quantity = value
        setQuantity(value);
    }

    function SetProductPrice(event: any) {
        var value = event.target.value;
        product.price = value
        setPrice(value);

        setIsPriceError(isNaN(value));
    }

    function SetProductTax(event: any) {
        var value = event.target.value;
        product.tax = value
        setTax(value);
    }

    function SetAllowFree(event: any) {
        var value = event.target.value;
        product.allowFree = value
        setAllowFree(value);
    }

    function SetProductBoothIdFilter(event: any) {
        var value = event.target.value;
        product.boothIdFilter = value
        setBoothIdFilter(value);
    }

    function SetCommission(event: any) {
        var value = event.target.value;
        product.commission = value
        setCommission(value);
    }

    function SetStartDate(event: any) {
        var value = dayjs(event).set("hour", 12);
        product.startDate = value
        setStartDate(value);
    }

    function SetPortalFeed(event: any) {
        var value = event.target.value;
        product.portalFeed = value
        setPortalFeed(value);
    }

    function SetXeroTracking(event: any) {
        var value = event.target.value;

        if (value === "noselect") {
            product.xeroTracking = null;
            setXeroTracking("noselect");
            setTrackingNotSet(true);
        }
        else {
            product.xeroTracking = JSON.parse(value);
            setXeroTracking(value);
            setTrackingNotSet(false);
        }
    }

    function SetHide(event: any) {
        var value = event.target.checked;
        product.hide = value;
        setHide(value);
    }


    function GetTrackingInfo(tracking: string): string {
        if (tracking === "noselect")
            return "";
        return (JSON.parse(tracking) as TrackingOptionModel).name;
    }

    function FPortalWorkAroundToolTip(): any {
        if (productCode === "F-Portal" && quantity === 42)
            return <Typography fontSize={15}>
                ✅Special workaround for Portal product.
            </Typography>;

        return "";
    }

    return (
        <Stack
            direction='row'
            justifyContent="flex-start"
            alignItems="flex-start"
            spacing={2}
            sx={{ marginBottom: '8px' }}>

            {/* Product Code */}
            <FormControl sx={{ minWidth: '160px', width: '160px' }}>
                <InputLabel>Product Code</InputLabel>
                <Select
                    value={productCode}
                    onChange={SetProductCode}
                    size="small"
                    label="Product Code"
                    defaultValue=""
                    sx={{ height: "50px" }}>
                    {productNames.map((pEl: any, pIndex: number) => (
                        <MenuItem value={pEl} key={pIndex}>{pEl}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Description */}
            <TextField
                multiline
                value={description}
                onChange={SetProductDescription}
                label="Description"
                sx={{ minWidth: 300 }}
                size="small"
                inputProps={{
                    style: {
                        minHeight: "33px"
                    }
                }}
            />

            {/* BoothId */}
            {boothIdFilterVisibility ?
                <Tooltip
                    placement="top"
                    title={
                        <Typography fontSize={24}>
                            {boothIdFilter.join(',')}
                        </Typography>
                    }>
                    <FormControl sx={{ minWidth: '180px', width: '180px' }}>
                        <InputLabel>Booth</InputLabel>
                        <Select
                            multiple
                            value={boothIdFilter ?? []}
                            onChange={SetProductBoothIdFilter}
                            label="Booth"
                            size="small"
                            sx={{ height: "50px" }}
                            input={<OutlinedInput label="Chip" />}
                            renderValue={(selected) => selected.join(', ')}>
                            {allBoothIds.map((boothEl: any, pIndex: number) => (
                                <MenuItem
                                    key={pIndex}
                                    value={boothEl.boothId}>
                                    <Checkbox checked={boothIdFilter.indexOf(boothEl.boothId) > -1} />
                                    <b>{boothEl.boothId}</b> {boothEl.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Tooltip>
                : null}

            {/* Quantity */}
            {quantityVisibility ?
                <Tooltip
                    placement="top"
                    title={FPortalWorkAroundToolTip()}>
                    <TextField
                        value={quantity}
                        onChange={SetProductQuantity}
                        label="Quantity"
                        sx={{ minWidth: 100, width: 100 }}
                        size="small"
                        inputProps={{
                            style: {
                                height: "33px"
                            }
                        }}
                    />
                </Tooltip> : null
            }
            {/* Price */}
            {
                priceVisibility ?
                    <TextField
                        value={price}
                        onChange={SetProductPrice}
                        label={`Price ${clientCurrency}`}
                        sx={{ minWidth: 100, width: 100 }}
                        size="small"
                        error={isPriceError}
                        inputProps={{
                            style: {
                                height: "33px"
                            }
                        }}
                    /> : null
            }

            {/* Tax */}
            {
                taxVisibility ?
                    <FormControl sx={{ minWidth: '80px', width: '80px' }}>
                        <InputLabel>Tax</InputLabel>
                        <Select
                            value={tax}
                            onChange={SetProductTax}
                            label="Tax"
                            size="small"
                            defaultValue={1}
                            sx={{ height: "50px" }}
                            input={<OutlinedInput label="Chip" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    <Chip label={selected} />
                                </Box>
                            )}>
                            {allTaxes.map((taxEl: any, pIndex: number) => (
                                <MenuItem
                                    key={pIndex}
                                    value={taxEl.id}>
                                    <b>{taxEl.id}</b> {taxEl.description}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    : null
            }

            {/* Allow Free */}
            {
                allowFreeVisibility ?
                    <TextField
                        value={allowFree}
                        onChange={SetAllowFree}
                        label="Includes X number free"
                        sx={{ minWidth: "150px", width: "150px" }}
                        size="small"
                        inputProps={{
                            style: {
                                height: "33px"
                            }
                        }}
                    /> : null
            }

            {/* Commission */}
            {
                allowCommissionVisibility && allCommission.length > 0 ?
                    <FormControl sx={{ minWidth: '120px', maxWidth: '160px' }}>
                        <InputLabel>Commission</InputLabel>
                        <Select
                            multiple
                            value={commission ?? []}
                            onChange={SetCommission}
                            label="Commission"
                            size="small"
                            sx={{ height: "50px" }}
                            input={<OutlinedInput label="Chip" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip key={value} label={value} />
                                    ))}
                                </Box>
                            )}>
                            {allCommission.map((commission, index: number) => (
                                <MenuItem
                                    key={index}
                                    value={commission.groupNumber}>
                                    {commission.groupName} - {commission.salesName} {commission.commissionPercent}%
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    : null
            }

            {/* Start date. For /month products */}
            {
                showStartDate ?
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            sx={{ minWidth: "150px", width: "150px" }}
                            format="DD/MM/YYYY"
                            value={startDate}
                            onChange={SetStartDate}
                        />
                    </LocalizationProvider>
                    : null
            }

            {/* Portal Feed */}
            {
                product.productCode.startsWith("F-") && allPortalFeeds.length > 0 ?
                    <FormControl sx={{ minWidth: '120px', maxWidth: '350px' }}>
                        <InputLabel>Portal Feed</InputLabel>
                        <Select
                            multiple
                            value={portalFeed ?? []}
                            onChange={SetPortalFeed}
                            label="Portal Feed"
                            size="small"
                            sx={{ minHeight: "50px" }}
                            input={<OutlinedInput label="Chip" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={value}
                                            size="small" />
                                    ))}
                                </Box>
                            )}>
                            {allPortalFeeds.map((portalEl: DisplayFeedModel, pIndex: number) => (
                                <MenuItem
                                    key={pIndex + 1}
                                    value={portalEl.feedName}>
                                    {portalEl.feedName} — <i>{portalEl.friendlyName}</i>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    :
                    null
            }

            {/* Xero */}
            <Tooltip
                placement="top"
                title={
                    <Typography fontSize={24}>
                        {GetTrackingInfo(xeroTracking)}
                    </Typography>
                }>
                <FormControl sx={{ minWidth: "100px", width: "100px" }}>
                    <InputLabel>Xero</InputLabel>
                    <Select
                        value={xeroTracking}
                        onChange={SetXeroTracking}
                        sx={{ height: "50px" }}
                        label="Xero"
                        size="small"
                        error={trackingNotSet}>
                        <MenuItem key="noselect" value="noselect">No tracking</MenuItem>
                        {allXeroTracking.map((item, index) => (
                            <MenuItem
                                key={index}
                                value={JSON.stringify(item)}>
                                {item.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Tooltip>

            {/* Hide */}
            {
                allowHideVisibility ?
                    <FormControlLabel
                        sx={{ minWidth: "125px" }}
                        label="Hide if empty"
                        control={
                            <Checkbox
                                sx={{ marginLeft: '-15px', }}
                                checked={hide}
                                onChange={SetHide}
                            />
                        } />
                    : null
            }

            <IconButton
                sx={{ color: 'red', left: '-10px', top: '2px' }}
                onClick={() => { removeProduct(product) }}>
                <DeleteForeverIcon sx={{ fontSize: 26 }} />
            </IconButton>

            <IconButton
                sx={{ color: 'black', left: '-25px', top: '5px' }}
                onClick={() => { duplicateProduct(product) }}>
                <ContentCopyIcon sx={{ fontSize: 20 }} />
            </IconButton>

            <IconButton
                sx={{ color: 'black', left: '-45px', top: '3px' }}
                onClick={() => { moveUpProduct(product) }}>
                <KeyboardArrowUpIcon sx={{ fontSize: 26 }} />
            </IconButton>

            <IconButton
                sx={{ color: 'black', left: '-65px', top: '3px' }}
                onClick={() => { moveDownProduct(product) }}>
                <KeyboardArrowDownIcon sx={{ fontSize: 26 }} />
            </IconButton>
        </Stack >
    )
}

export default BillingProduct;